import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { getLoginImage, uploadLoginImage, deleteLoginImage } from "../../services/imageService";
import { Icon } from "@iconify/react/dist/iconify.js";

const LoginImageManageLayer = () => {
  const [activeImage, setActiveImage] = useState(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  // Load current image from API on mount
  const fetchImage = async () => {
    setFetching(true);
    try {
      const res = await getLoginImage();
      if (res?.status === "success" && res?.data) {
        let imgUrl = null;
        if (Array.isArray(res.data)) {
          if (res.data.length > 0) {
            imgUrl = res.data[0].image_url;
          }
        } else if (typeof res.data === "object") {
          imgUrl = res.data.image_url;
        }
        setActiveImage(imgUrl || null);
      } else {
        setActiveImage(null);
      }
    } catch (error) {
      toast.error("Failed to load current login image");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchImage();
  }, []);

  // Handle local file selection change (for preview)
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreviewUrl(URL.createObjectURL(selectedFile));
    }
  };

  // Upload new image
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please select an image file to upload");
      return;
    }

    setLoading(true);
    try {
      const res = await uploadLoginImage(file);
      if (res?.status === "success" || res?.status === true) {
        toast.success(res.message || "Image uploaded successfully");
        const newUrl = res.url || (res.data ? (Array.isArray(res.data) ? (res.data.length > 0 ? res.data[0].image_url : null) : res.data.image_url) : null);
        if (newUrl) {
          setActiveImage(newUrl);
        } else {
          // Refetch to be certain
          await fetchImage();
        }
        setFile(null);
        setPreviewUrl(null);
        const fileInput = document.getElementById("login_image_input");
        if (fileInput) fileInput.value = "";
      } else {
        toast.error(res?.message || "Failed to upload image");
      }
    } catch (error) {
      toast.error("Something went wrong during upload");
    } finally {
      setLoading(false);
    }
  };

  // Delete current image
  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete the login image?")) {
      return;
    }

    setLoading(true);
    try {
      // Pass the current file if needed, or null for normal delete
      const res = await deleteLoginImage(file);
      if (res?.status === "success" || res?.status === true) {
        toast.success(res.message || "Image deleted successfully");
        setActiveImage(null);
        setFile(null);
        setPreviewUrl(null);
        const fileInput = document.getElementById("login_image_input");
        if (fileInput) fileInput.value = "";
      } else {
        toast.error(res?.message || "Failed to delete image");
      }
    } catch (error) {
      toast.error("Something went wrong during deletion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
        <h6 className="text-lg fw-semibold mb-0">Login Page Image Management</h6>
      </div>
      <div className="card-body p-24">
        <div className="row gy-4">
          
          {/* Form Section */}
          <div className="col-lg-6">
            <form onSubmit={handleUpload}>
              <div className="mb-24">
                <label htmlFor="login_image_input" className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Choose Login Image <span className="text-danger-600">*</span>
                </label>
                <input
                  type="file"
                  className="form-control radius-8"
                  id="login_image_input"
                  accept="image/*"
                  onChange={handleFileChange}
                />
                <div className="form-text text-muted mt-8">
                  Recommended size: 917x917 pixels. Supported formats: JPG, PNG, WEBP.
                </div>
              </div>

              <div className="d-flex flex-wrap gap-12 mt-4">
                <button
                  type="submit"
                  className="btn btn-primary-600 radius-8 px-20 py-11 d-flex align-items-center gap-2"
                  disabled={loading || fetching || !file}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Icon icon="lucide:upload" className="text-xl" />
                      Upload / Update Image
                    </>
                  )}
                </button>

                {activeImage && (
                  <button
                    type="button"
                    className="btn btn-danger-600 radius-8 px-20 py-11 d-flex align-items-center gap-2"
                    onClick={handleDelete}
                    disabled={loading || fetching}
                  >
                    <Icon icon="lucide:trash-2" className="text-xl" />
                    Delete Active Image
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Preview Section */}
          <div className="col-lg-6">
            <div className="border radius-12 p-16 h-100 d-flex flex-column align-items-center justify-content-center bg-neutral-50 min-h-300-px">
              <h6 className="text-md fw-semibold text-primary-light mb-16 w-100 text-center">
                Image Preview
              </h6>
              
              {fetching ? (
                <div className="d-flex flex-column align-items-center justify-content-center">
                  <div className="spinner-border text-primary mb-12" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="text-sm text-secondary-light">Fetching active image...</span>
                </div>
              ) : previewUrl ? (
                <div className="w-100 text-center">
                  <span className="badge bg-warning-600 text-sm mb-12">New Selection Preview</span>
                  <div className="position-relative d-inline-block mx-auto border radius-8 overflow-hidden bg-white shadow-sm" style={{ maxWidth: "250px" }}>
                    <img
                      src={previewUrl}
                      alt="Preview Selection"
                      className="img-fluid"
                      style={{ maxHeight: "250px", objectFit: "contain" }}
                    />
                  </div>
                </div>
              ) : activeImage ? (
                <div className="w-100 text-center">
                  <span className="badge bg-success-600 text-sm mb-12">Active Login Page Image</span>
                  <div className="position-relative d-inline-block mx-auto border radius-8 overflow-hidden bg-white shadow-sm" style={{ maxWidth: "250px" }}>
                    <img
                      src={activeImage}
                      alt="Active Login Page"
                      className="img-fluid"
                      style={{ maxHeight: "250px", objectFit: "contain" }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "assets/images/auth/auth-img.png";
                      }}
                    />
                  </div>
                  <div className="text-xs text-muted mt-12 break-all" style={{ maxWidth: "100%", wordBreak: "break-all" }}>
                    {activeImage}
                  </div>
                </div>
              ) : (
                <div className="text-center py-40">
                  <Icon icon="lucide:image" className="text-5xl text-neutral-300 mb-12" />
                  <p className="text-sm text-secondary-light mb-0">No active login image found on server.</p>
                  <p className="text-xs text-muted mt-4">The login page will default to the standard theme placeholder image.</p>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default LoginImageManageLayer;
